import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import SearchOverlay from './SearchOverlay.jsx'
import { useMe } from '../lib/me.js'
import { isDoctorOnly } from '../lib/role.js'

const SearchCtx = createContext({ open: false, openSearch: () => {}, closeSearch: () => {}, enabled: true })

export function useSearch() {
  return useContext(SearchCtx)
}

export default function SearchProvider({ children }) {
  const me = useMe()
  // Doctor-only (SSO) sessions can't browse/search patients — they enter a single
  // patient via the doctor portal. Disable ⌘K + the overlay entirely for them.
  const enabled = !isDoctorOnly(me)

  const [open, setOpen] = useState(false)
  const openSearch = useCallback(() => { if (enabled) setOpen(true) }, [enabled])
  const closeSearch = useCallback(() => setOpen(false), [])

  // Force-close if a session turns doctor-only while the overlay is open.
  useEffect(() => { if (!enabled) setOpen(false) }, [enabled])

  // Global ⌘K / Ctrl+K toggles the overlay from anywhere (employees only).
  useEffect(() => {
    if (!enabled) return
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setOpen((v) => !v)
      } else if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, enabled])

  return (
    <SearchCtx.Provider value={{ open, openSearch, closeSearch, enabled }}>
      {children}
      {enabled && open && <SearchOverlay onClose={closeSearch} />}
    </SearchCtx.Provider>
  )
}
